import { isFeatureFlagEnabled } from '@ipfabric/ui-components/utils/featureFlags';
import {
  type DeviceViewSettingsTable,
  type NodeGraphExtra,
  type NodeType,
  type ProtocolEdgeType,
  isCloudNetworkNodeType,
} from '@ipfabric/utils';
import {
  DEVICE_ATTRIBUTE_DETAIL_TAB,
  DEVICE_DETAIL_TAB,
  INTENT_CHECK_DETAIL_TAB,
  PATH_INSPECTOR_DETAIL_TAB,
  RESOURCE_HIERARCHY_DETAIL_TAB,
  STP_DETAIL_TAB,
} from 'components/DeviceView/deviceViewConstants';
import type {
  DeviceViewElementType,
  DeviceViewLabelsTab,
} from 'components/DeviceView/deviceViewTypes';
import { isCloudConstructNode } from './cloudConstruct';
import { hasNodeDetailTab } from './hasNodeDetailTab';

export type DeviceViewTabOrderArgs = {
  deviceViewElement: DeviceViewElementType;
  deviceViewEntityTables: DeviceViewSettingsTable[];
  extra: NodeGraphExtra | null;
  intentCheckIsApplied: boolean;
  isPathLookupNode: boolean;
  labelsTabs: DeviceViewLabelsTab[];
  nodeHasPathInspectorDetail: boolean;
  nodeType: NodeType | null;
  protocol?: ProtocolEdgeType;
};

const hasAttributesTab = (
  nodeType: NodeType | null,
  extra: NodeGraphExtra | null,
): boolean =>
  !isCloudConstructNode(nodeType, extra) ||
  (nodeType !== null &&
    isCloudNetworkNodeType(nodeType) &&
    !isFeatureFlagEnabled('ENABLE_NEW_CLOUD_MODEL'));

const getNodeExplorerTabIds = ({
  extra,
  isPathLookupNode,
  labelsTabs,
  nodeHasPathInspectorDetail,
  nodeType,
}: Pick<
  DeviceViewTabOrderArgs,
  | 'extra'
  | 'isPathLookupNode'
  | 'labelsTabs'
  | 'nodeHasPathInspectorDetail'
  | 'nodeType'
>): string[] => {
  const tabIds: string[] = [];
  if (isPathLookupNode && nodeHasPathInspectorDetail) {
    tabIds.push(PATH_INSPECTOR_DETAIL_TAB);
  }
  if (hasNodeDetailTab(nodeType)) {
    tabIds.push(DEVICE_DETAIL_TAB);
  }
  if (isCloudConstructNode(nodeType, extra)) {
    tabIds.push(RESOURCE_HIERARCHY_DETAIL_TAB);
  }
  for (const tab of labelsTabs) {
    tabIds.push(tab.columnKey);
  }
  if (hasAttributesTab(nodeType, extra)) {
    tabIds.push(DEVICE_ATTRIBUTE_DETAIL_TAB);
  }
  return tabIds;
};

// Must match tab JSX in DeviceViewContentDetailHeader. ScrollingTabs indexes
// children in render order, including optional intent/path/hierarchy prefixes.
export const getDeviceViewTabOrder = ({
  deviceViewElement,
  deviceViewEntityTables,
  extra,
  intentCheckIsApplied,
  isPathLookupNode,
  labelsTabs,
  nodeHasPathInspectorDetail,
  nodeType,
  protocol,
}: DeviceViewTabOrderArgs): string[] => {
  if (deviceViewElement === 'labels') {
    return labelsTabs.map((tab) => tab.columnKey);
  }

  const tabIds: string[] = [];

  if (intentCheckIsApplied) {
    tabIds.push(INTENT_CHECK_DETAIL_TAB);
  }

  if (deviceViewElement === 'node') {
    tabIds.push(
      ...getNodeExplorerTabIds({
        extra,
        isPathLookupNode,
        labelsTabs,
        nodeHasPathInspectorDetail,
        nodeType,
      }),
    );
  }

  if (protocol === 'stp') {
    tabIds.push(STP_DETAIL_TAB);
  }

  for (const table of deviceViewEntityTables) {
    tabIds.push(table.webEndpoint);
  }

  return tabIds;
};

export const getTabsIndex = (
  tabIds: string[],
  selectedTable: string | null,
): number => {
  if (selectedTable === null || selectedTable === '') {
    return 0;
  }
  const index = tabIds.indexOf(selectedTable);
  return index === -1 ? 0 : index;
};
